




































import tradingService from '@/services/tradingService';
import { Component, Vue, Prop } from 'vue-property-decorator';
import { Action, Getter } from 'vuex-class';
import { centsToEuro } from '@/filters/currency';
import { Trade } from '@/store/sellOffer/types';
import { downloadCsvFile, json2csv } from '@/utils/csv';

import ExporoTable from '@/components/common/Table.vue';
import ExporoSelect from '@/components/common/Select.vue';

@Component({
  name: 'my-trades-view',
  filters: {
    centsToEuro,
  },
  components: {
    ExporoSelect,
    ExporoTable,
  },
})
export default class MyTradesView extends Vue {
  @Action('appState/startLoader') startLoader;
  @Action('appState/stopLoader') stopLoader;
  @Getter('appState/isLoading') isLoading?: boolean;
  @Getter('profile/userId') userId?: number;

  trades: Trade[] = null;
  selectedState: string = 'open';

  get tradeStates() {
    return [
      { name: this.$t('myTrades.filters.All'), value: 'all' },
      { name: this.$t('myTrades.filters.Open'), value: 'open' },
      { name: this.$t('myTrades.filters.Closed'), value: 'closed' },
    ];
  }

  get tradeStateName(){
    return this.tradeStates
        .find(state => state.value === this.selectedState)
        .name;
  }

  get tableData(): Array<object> {
    if (this.trades) {
      return this.trades
        .filter(trade => trade.sell_offer?.brand.toLowerCase() === 'exporo')
        .map((transaction) => ({
        [this.$t('myTrades.table.header', { context: 'id' })]: {
          text: transaction.id,
          value: transaction.id,
        },
        [this.$t('myTrades.table.header', { context: 'type' })]: {
          text: this.setTransactionIcons(transaction),
          value:
            transaction.user_id !== this.userId
              ? this.$t('myTrades.types.sell')
              : this.$t('myTrades.types.buy'),
        },
        [this.$t('myTrades.table.header', { context: 'contractTitle' })]: {
          text: transaction.sell_offer.asset.title,
          value: transaction.sell_offer.asset.title,
        },
        [this.$t('myTrades.table.header', { context: 'unitPrice' })]: {
          text: this.$options.filters.centsToEuro(transaction.unit_price),
          value: transaction.unit_price,
        },
        [this.$t('myTrades.table.header', { context: 'pieces' })]: {
          text: transaction.pieces,
          value: transaction.pieces,
        },
        [this.$t('myTrades.table.header', { context: 'total' })]: {
          text: this.$options.filters.centsToEuro(transaction.total),
          value: transaction.total,
        },
        [this.$t('myTrades.table.header', { context: 'state' })]: {
          text:
            this.$t(`myTrades.states.${transaction.state}`) ||
            transaction.state,
          value: transaction.state,
        },
      }));
    }

    return [];
  }

  async mounted() {
    this.getTrades();
  }

  async getTrades() {
    try {
      this.startLoader();

      switch (this.selectedState) {
        case 'all':
          this.trades = [
            ...(await tradingService.getActiveTrades()).data,
            ...(await tradingService.getTradeHistory()).data,
          ];
          break;
        case 'open':
          this.trades = (await tradingService.getActiveTrades()).data;
          break;
        case 'closed':
          this.trades = (await tradingService.getTradeHistory()).data;
          break;
      }
    } catch (error) {
      console.error(error);
    }
    this.stopLoader();
  }

  setTransactionIcons(transaction: Trade) {
    const isSell = transaction.user_id !== this.userId;

    const color = 'primary';
    const icon = 'sync_alt';

    const transactionType = isSell
      ? this.$t('myTrades.types.sell')
      : this.$t('myTrades.types.buy');

    return `<i title="${transactionType}" class="font-size-medium material-icons color-text-${color}">
              ${icon}
            </i> ${transactionType}`;
  }

  async onHistoryClick() {
    try {
      // Convert trades data into processed data.
      const data = this.trades
        .filter(trade => trade.sell_offer?.brand.toLowerCase() === 'exporo')
        .map((trade) => {
          return {
            id: trade.id,
            security_wallet: trade.security_wallet,
            asset_id: trade.sell_offer.asset.asset_id,
            asset_name: trade.sell_offer.asset.title,
            state: trade.state,
            pieces: trade.pieces,
            trade_price: new Intl.NumberFormat('de-DE', {
              minimumFractionDigits: 2,
            }).format(trade.unit_price / 100),
            total: new Intl.NumberFormat('de-DE', {
              minimumFractionDigits: 2,
            }).format(trade.total / 100),
            trade_closed_at: trade.updated_at,
          };
        });

      // Convert data into json.
      const headers = [
        'id',
        'security_wallet',
        'asset_id',
        'asset_name',
        'state',
        'pieces',
        'trade_price',
        'total',
        'trade_closed_at',
      ];
      const json = json2csv(data, headers);
      
      downloadCsvFile(json, `${this.tradeStateName}_Trades_History_${Date.now()}.csv`, 'csv');
    } catch (error) {
      console.error(error);
    }
  }

  onTradeStateUpdate(value) {
    this.selectedState = value;
    this.getTrades();
  }
}
